<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="swap-dialog__dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="swap-dialog-form" @submit.prevent="onSubmit">
        <div class="swap-dialog-form-body">
          <v-toolbar flat height="76" class="swap-dialog__toolbar">
            <v-toolbar-title class="swap-dialog-toolbar-title"
              >{{ editedSwap ? 'Edit' : 'New' }}
              {{ tenantTexts.swapDialog.swap }}</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <div class="swap-dialog-form-body">
            <v-container class="pb-2">
              <h5 class="mb-0">What we will be working on?</h5>
            </v-container>
            <v-container class="swap-dialog-card-container">
              <bh-text-input
                :counter="1000"
                :show-counter-in-range="10"
                input-background-class="bhWhite"
                label="Swapping from"
                name="description"
                :value="(newSwapData && newSwapData.description) || ''"
                :readonly="readonly"
                :validate="true"
                :rules="[
                  value =>
                    !!value ||
                    'Swapping from field cannot be empty. Please provide a value',
                  value =>
                    (value || '').length <= 1000 || 'Max 1,000 characters',
                ]"
                @input-text="onInput({ name: 'description', value: $event })"
              />
            </v-container>
            <v-container class="swap-dialog-card-container">
              <bh-text-input
                :counter="1000"
                :show-counter-in-range="10"
                input-background-class="bhWhite"
                label="Swapping to"
                name="goal"
                :value="(newSwapData && newSwapData.goal) || ''"
                :readonly="readonly"
                :validate="true"
                :rules="[
                  value =>
                    !!value ||
                    'Swapping to field cannot be empty. Please provide a value',
                  value =>
                    (value || '').length <= 1000 || 'Max 1,000 characters',
                ]"
                @input-text="onInput({ name: 'goal', value: $event })"
              />
            </v-container>
            <v-container class="swap-dialog-card-container mb-4">
              <bh-text-area
                :counter="1000"
                :show-counter-in-range="10"
                label="Tactics (optional)"
                name="tactic"
                :value="(newSwapData && newSwapData.tactic) || ''"
                :readonly="readonly"
                :validate="true"
                :rules="[
                  value =>
                    (value || '').length <= 1000 || 'Max 1,000 characters',
                ]"
                @input-text="onInput({ name: 'tactic', value: $event })"
              />
            </v-container>
            <v-container>
              <span class="bh-text-input__label"
                >Number of time you want to practice this?</span
              >
              <div class="d-flex align-center">
                <bh-number-input
                  name="repeatGoal"
                  v-model="repeatGoalValue"
                  :value="
                    newSwapData && newSwapData.repeatGoal
                      ? newSwapData.repeatGoal
                      : 1
                  "
                  :readonly="readonly"
                  :minNumber="1"
                  :maxNumber="100"
                  input-class="rounded-0"
                  @input="onInput({ name: 'repeatGoal', value: $event })"
                ></bh-number-input>
              </div>
            </v-container>
            <v-container v-if="editedSwap">
              <dialog-select
                label="Status"
                name="status"
                :items="statusOptions"
                @input="onInput"
                :value="
                  newSwapData && newSwapData.status ? newSwapData.status : 'NEW'
                "
              />
            </v-container>
            <v-container class="pb-0">
              <h5 class="mb-0">Category - Starting Point</h5>
            </v-container>
            <v-container>
              <v-row>
                <v-col>
                  <dialog-select
                    label="Difficulty Level"
                    name="difficultLevel"
                    :items="difficultLevelOptions"
                    @input="onInput"
                    :value="newSwapData.difficultLevel"
                  />
                </v-col>
                <v-col>
                  <dialog-select
                    label="Impact Level"
                    name="impactLevel"
                    :items="impactLevelOptions"
                    @input="onInput"
                    :value="newSwapData.impactLevel"
                  />
                </v-col>
              </v-row>
            </v-container>
            <v-container v-if="!editedSwap" class="pb-0">
              <h5 class="mb-0">Media (optional)</h5>
            </v-container>
            <v-container
              class="swap-dialog-card-container pt-0"
              v-if="!editedSwap"
            >
              <thumbnail-picker
                label=""
                :current-file="currentFile"
                @thumbnail-picked="setThumbnail"
                :readonly="readonly"
              />
            </v-container>
            <v-container
              class="d-flex justify-space-between align-center swap-dialog__tab--footer"
            >
              <v-spacer />
              <v-btn
                rounded
                color="primary"
                elevation="0"
                :loading="submitLoading"
                @click="onSubmit"
                :disabled="!formIsValidCheck"
              >
                {{ editedSwap ? 'Update' : 'Add' }}
                {{ tenantTexts.swapDialog.swap }}
              </v-btn>
            </v-container>
          </div>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import TenantTextsMixin from '@/mixins/tenantTexts'
import { debounce } from '@/helpers/debounce'
import BhNumberInput from '@/components/inputs/BhNumberInput.vue'
import DialogSelect from '@/components/forms/DialogSelect.vue'
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'
import BhTextArea from '@/components/inputs/BhTextArea.vue'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import { swapStatusName } from '@/helpers/swapCategoryName'

const emptyForm = {
  description: '',
  goal: '',
  repeatGoal: 1,
  impactLevel: 'MAJOR',
  difficultLevel: 'EASY',
  tactic: '',
  thumbnailUrl: null,
  mediaUrl: null,
  thumbnailValue: null,
  status: 'NEW',
}

export default {
  name: 'SwapDialog',
  components: {
    BhNumberInput,
    DialogSelect,
    ThumbnailPicker,
    BhTextArea,
    BhTextInput,
  },
  mixins: [TenantTextsMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    editedSwap: {
      type: [Boolean, Object],
      default: false,
    },
    submitLoadingRefreshKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      dialog: false,
      submitLoading: false,
      newSwapData: {
        description: '',
        goal: '',
        repeatGoal: 1,
        impactLevel: 'MAJOR',
        difficultLevel: 'EASY',
        tactic: '',
        thumbnailUrl: null,
        mediaUrl: null,
        thumbnailValue: null,
        status: 'NEW',
      },
      repeatGoalValue: 1,
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true
        this.setupSwapData()
      } else {
        this.dialog = false
      }
    },
    editedSwap: function(val) {
      this.setupSwapData()
    },
    submitLoadingRefreshKey: function(val) {
      this.submitLoading = false
    },
  },
  created() {
    this.setupSwapData()
  },
  mounted() {},
  computed: {
    currentFile() {
      if (!this.newSwapData?.thumbnailUrl) {
        return null
      }
      return {
        thumbnailUrl: this.newSwapData?.thumbnailUrl,
        mediaUrl: this.newSwapData?.mediaUrl || null,
      }
    },
    difficultLevelOptions() {
      return [
        {
          text: 'easy',
          value: 'EASY',
        },
        {
          text: 'hard',
          value: 'HARD',
        },
      ]
    },
    impactLevelOptions() {
      return [
        {
          text: 'major',
          value: 'MAJOR',
        },
        {
          text: 'minor',
          value: 'MINOR',
        },
      ]
    },
    statusOptions() {
      return [
        {
          text: swapStatusName('NEW'),
          value: 'NEW',
        },
        {
          text: swapStatusName('IN_PROGRESS'),
          value: 'IN_PROGRESS',
        },
        {
          text: swapStatusName('DONE'),
          value: 'DONE',
        },
        {
          text: swapStatusName('REJECTED'),
          value: 'REJECTED',
        },
      ]
    },
    formIsValidCheck() {
      return (
        this.newSwapData?.description?.length > 0 &&
        this.newSwapData?.description?.length <= 1000 &&
        this.newSwapData?.goal?.length > 0 &&
        this.newSwapData?.goal?.length <= 1000 &&
        this.newSwapData?.repeatGoal > 0 &&
        this.newSwapData?.repeatGoal <= 100 &&
        ['EASY', 'HARD'].includes(this.newSwapData?.difficultLevel) &&
        ['MINOR', 'MAJOR'].includes(this.newSwapData?.impactLevel) &&
        (!this.newSwapData?.tactic ||
          (this.newSwapData.tactic && this.newSwapData.tactic.length <= 1000))
      )
    },
  },
  methods: {
    closeDialog() {
      this.$emit('close-swap-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    setupSwapData() {
      this.submitLoading = false
      if (this.editedSwap) {
        for (const key of Object.keys(this.editedSwap)) {
          this.newSwapData[key] = this.editedSwap[key]
          if (key === 'repeatGoal') this.repeatGoalValue = this.editedSwap[key]
        }
      } else {
        this.newSwapData = { ...emptyForm }
      }
    },
    onInput({ name, value }) {
      let text = value
      if (name === 'tactic') {
        const isRemoving = text.length < this.newSwapData[name].length

        const newText = text
          .replace(/\n\n/g, '\n')
          .replace(/\n• \n/g, '\n')
          .split(/\n/g)
          .map(el => el.replace('•', '').trimStart())
          .filter(el => {
            if (!isRemoving) {
              return true
            }

            return el.length > 0
          })
          .join('\n• ')

        text = `• ${newText}`
      }
      this.newSwapData[name] = text
    },
    setThumbnail(thumbnail = null) {
      if (thumbnail === null) {
        // remove
        this.newSwapData.thumbnailValue = { remove: true }
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.newSwapData.thumbnailValue = { mlSourceResourceId: file.id }
        } else if (type === 'file-picker') {
          this.newSwapData.thumbnailUrl = null
          this.newSwapData.mediaUrl = null
          this.newSwapData.thumbnailValue = file
        }
      }
    },
    onSubmit() {
      this.submitLoading = true
      if (this.editedSwap) {
        this.$emit('on-edit', { ...this.newSwapData })
      } else {
        this.$emit('on-submit', { ...this.newSwapData })
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.swap-dialog {
  &__dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }

  &__toolbar {
    flex-grow: 0;
  }

  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }

  &-form {
    .bh-text-input__label,
    .category-name,
    .thumbnail-picker__header strong,
    .dialog-select-label,
    .bh-text-area__label {
      font-size: 14px;
      line-height: 20px;
      font-weight: 700;
      color: #6c7480 !important;
    }
  }
}
</style>
